import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {
  ModeleSavLigne,
  SavControleSaisie,
  SavDetailResponse,
  SavReferentiels,
} from '../../../../components/sav/Models'
import {
  controlerSavLigne,
  getSavDestinationProposee,
  savErreur,
} from '../../../../components/sav/requests'
import {SavAjoutDocument} from './SavDocuments'

type Props = {
  dossierId: number
  ligne: ModeleSavLigne
  referentiels: SavReferentiels
  onMaj: (detail: SavDetailResponse) => void
  onValidee: (detail: SavDetailResponse) => void
}

const ouiNon = (
  valeur: boolean | null,
  onChange: (v: boolean) => void,
  libelles: [string, string] = ['Oui', 'Non']
) => (
  <div className='btn-group w-100'>
    {[true, false].map((v, i) => (
      <button
        key={String(v)}
        type='button'
        className={`btn ${valeur === v ? (v ? 'btn-success' : 'btn-danger') : 'btn-light'}`}
        onClick={() => onChange(v)}
      >
        {libelles[i]}
      </button>
    ))}
  </div>
)

// Formulaire « Laver et contrôler la pièce » (écran 6, repris dans la fiche dossier)
const SavControleForm = ({dossierId, ligne, referentiels, onMaj, onValidee}: Props) => {
  const [saisie, setSaisie] = useState<SavControleSaisie>({
    lavageEffectue: ligne.lavageEffectue,
    etatPieceConstate: ligne.etatPieceConstate?.id ?? null,
    controleFonde: ligne.controleFonde ?? null,
    pieceReutilisable: ligne.pieceReutilisable ?? null,
    destination: ligne.destination?.id ?? null,
    commentaireControle: ligne.commentaireControle ?? '',
  })
  // La destination suit la présélection tant que le contrôleur ne l'a pas changée
  const [destinationChoisie, setDestinationChoisie] = useState(false)
  const [motifRefus, setMotifRefus] = useState('')
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    if (destinationChoisie || saisie.controleFonde === null) return
    getSavDestinationProposee(saisie.controleFonde, saisie.pieceReutilisable)
      .then((d) => d?.id && setSaisie((s) => ({...s, destination: d.id ?? null})))
      .catch(() => undefined)
  }, [saisie.controleFonde, saisie.pieceReutilisable, destinationChoisie])

  const maj = (changement: Partial<SavControleSaisie>) => setSaisie((s) => ({...s, ...changement}))
  const destination = referentiels.destinations.find((d) => d.id === saisie.destination)
  const complet =
    saisie.lavageEffectue &&
    saisie.etatPieceConstate &&
    saisie.controleFonde !== null &&
    saisie.pieceReutilisable !== null &&
    saisie.destination

  const envoyer = (valider: boolean) => {
    setEnvoi(true)
    controlerSavLigne(ligne.id, saisie, {
      valider,
      motifRefus: motifRefus ? Number(motifRefus) : null,
    })
      .then((detail) => {
        toast.success(valider ? 'Pièce validée' : 'Contrôle enregistré')
        valider ? onValidee(detail) : onMaj(detail)
      })
      .catch((e) => toast.error(savErreur(e)))
      .finally(() => setEnvoi(false))
  }

  return (
    <div className='rounded p-5' style={{border: '2px dashed #1B84FF'}}>
      <h4 className='mb-5'>Laver et contrôler la pièce</h4>
      <div className='row g-4'>
        <div className='col-md-6'>
          <label className='form-check form-check-custom form-check-solid'>
            <input
              className='form-check-input'
              type='checkbox'
              checked={saisie.lavageEffectue}
              onChange={(e) => maj({lavageEffectue: e.target.checked})}
            />
            <span className='form-check-label fw-bold required'>Lavage effectué</span>
          </label>
        </div>
        <div className='col-md-6'>
          <label className='form-label required'>État constaté</label>
          <select
            className='form-select'
            value={saisie.etatPieceConstate ?? ''}
            onChange={(e) =>
              maj({etatPieceConstate: e.target.value ? Number(e.target.value) : null})
            }
          >
            <option value=''>Choisir</option>
            {referentiels.etatsPiece.map((etat) => (
              <option key={etat.id} value={etat.id}>
                {etat.libelle}
              </option>
            ))}
          </select>
        </div>
        <div className='col-md-6'>
          <label className='form-label required'>La réclamation est-elle fondée ?</label>
          {ouiNon(saisie.controleFonde, (v) => maj({controleFonde: v}))}
          {saisie.controleFonde === false && (
            <div className='text-muted fs-7 mt-1'>
              Non : réexpédition au client, à l'adresse de renvoi du dossier.
            </div>
          )}
        </div>
        <div className='col-md-6'>
          <label className='form-label required'>La pièce est-elle réutilisable ?</label>
          {ouiNon(saisie.pieceReutilisable, (v) => maj({pieceReutilisable: v}))}
          {saisie.controleFonde && saisie.pieceReutilisable !== null && (
            <div className='text-muted fs-7 mt-1'>
              {saisie.pieceReutilisable
                ? 'Oui : remise en stock dans ATEMO par la Logistique'
                : 'Non : destruction (KO PC NCF)'}
            </div>
          )}
        </div>
        <div className='col-md-6'>
          <label className='form-label required'>Destination</label>
          <select
            className='form-select'
            value={saisie.destination ?? ''}
            onChange={(e) => {
              setDestinationChoisie(true)
              maj({destination: e.target.value ? Number(e.target.value) : null})
            }}
          >
            <option value=''>Choisir</option>
            {referentiels.destinations.map((d) => (
              <option key={d.id} value={d.id}>
                {d.libelle}
              </option>
            ))}
          </select>
        </div>
        {saisie.controleFonde === false && (
          <div className='col-md-6'>
            <label className='form-label'>Motif de refus</label>
            <select
              className='form-select'
              value={motifRefus}
              onChange={(e) => setMotifRefus(e.target.value)}
            >
              <option value=''>Choisir</option>
              {referentiels.motifsRefus.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.libelle}
                </option>
              ))}
            </select>
            <div className='text-muted fs-7 mt-1'>
              Obligatoire si aucune pièce du dossier n'est fondée.
            </div>
          </div>
        )}
        {destination?.comportement === 'REMISE_EN_STOCK' && (
          <div className='col-12'>
            <div className='alert alert-info mb-0'>
              Remise en stock : à saisir dans ATEMO par la Logistique. À la validation, une tâche
              lui est créée dans Mes actions.
            </div>
          </div>
        )}
        <div className='col-12'>
          <label className='form-label'>Commentaire de contrôle</label>
          <textarea
            className='form-control form-control-solid'
            rows={2}
            value={saisie.commentaireControle}
            onChange={(e) => maj({commentaireControle: e.target.value})}
          />
        </div>
      </div>
      <div className='d-flex flex-wrap justify-content-between align-items-center gap-3 mt-5'>
        <SavAjoutDocument
          dossierId={dossierId}
          type='PHOTO_CONTROLE'
          ligne={ligne.id}
          libelle='Ajouter des photos de contrôle'
          accept='image/*'
          onAjoute={onMaj}
        />
        <div className='d-flex gap-3'>
          <button className='btn btn-light' disabled={envoi} onClick={() => envoyer(false)}>
            Enregistrer
          </button>
          <button
            className='btn btn-success'
            disabled={envoi || !complet}
            onClick={() => envoyer(true)}
          >
            Valider la pièce
          </button>
        </div>
      </div>
      <div className='text-muted fs-7 mt-3'>
        Le dossier passe à « À solder » quand toutes ses pièces sont contrôlées et qu'au moins une
        est fondée.
      </div>
    </div>
  )
}

export default SavControleForm
